import React, { useEffect } from 'react';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { useIsMobile } from '@amzn/storm-ui';
import { ThemeProvider } from 'styled-components';
import { connect } from 'react-redux';
import { RootState } from 'src/app/store';
import { Flag, selectIsMobileWeb, selectObjectInfo } from 'src/app/slice';
import didElementLoad from 'src/commons/utils/didElementLoad';
import { Attributes } from './Attributes/Attributes';
import { Actions } from './Actions/Actions';
import {
  ActionsAndAttributesWrapper,
  Container,
  PageTitle,
  PageTitleWrap,
  popoverThemeOverride,
  TitleText,
} from './styles';
import { AttributeTypes } from './types';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { selectRenderTagInPageName } from 'src/features/nav/store/slice';
import { PageTitleIcon } from './PageTitleIcon/PageTitleIcon';
import { getClickEventHandler } from 'src/commons/utils';
import { PageTitleIconContextProvider } from 'src/features/header/components/PageHeader/PageTitleIcon/PageTitleIconContext';
import { formatFlagType } from 'src/commons/components/LaunchFlag/LaunchFlagUtils';
import { LaunchFlag } from 'src/commons/components/LaunchFlag/LaunchFlag';

const Title = ({
  objectType,
  objectName,
  pageName,
  isMobile,
  pageNameTag,
  pageTitleIconProps,
}) => {
  if (pageName && !objectType && !objectName) {
    return null;
  }

  let pageTitleIconEventHandler;
  if (pageTitleIconProps) {
    pageTitleIconEventHandler = getClickEventHandler(
      pageTitleIconProps,
      AACChromeAPI.getPageTitleIconEventTarget(),
    );
  }

  return (
    <PageTitleIconContextProvider>
      <TitleText
        renderAs="h1"
        size="large"
        typeface="ember"
        data-ccx-e2e-id="aac-object-type"
      >
        {objectType && `${objectType}:`}
        <PageTitle
          isMobile={isMobile}
          hasObjectType={objectType}
          data-ccx-e2e-id="aac-page-name"
        >
          {objectName || pageName}
        </PageTitle>
        {pageNameTag}
        {pageTitleIconProps && (
          <PageTitleIcon
            {...pageTitleIconProps}
            eventHandler={pageTitleIconEventHandler}
            pageNameTagExists={!!pageNameTag}
          />
        )}
      </TitleText>
    </PageTitleIconContextProvider>
  );
};

export const ObjectInfo: React.VFC<{
  attributes: Array<AttributeTypes>;
  pageName?: string;
  objectType?: string;
  objectName?: string;
  attributeErrorMessage?: string;
  actionErrorMessage?: string;
  isMobileDevice: boolean;
  pageTitleTag?: Flag | null;
}> = ({
  attributes,
  objectType,
  objectName,
  attributeErrorMessage,
  actionErrorMessage,
  isMobileDevice,
  pageTitleTag,
}) => {
  const chromeConfig = useChromeConfigContext();
  const isMobile = useIsMobile();
  const pageName = chromeConfig.pageHeader?.pageName;
  const actions = chromeConfig.pageHeader?.objectInfo?.actions?.list || [];
  const hasAttributes = attributes && attributes.length > 0;
  const hasActions = actions && actions.length > 0;
  const hasPageTitle =
    (!!objectType && objectType.length > 0) ||
    (!!objectName && objectName.length > 0);
  const pageTitleIcon = chromeConfig.pageHeader?.pageTitleIcon;
  const breadcrumbs = chromeConfig.breadcrumbs || [];

  useEffect(() => {
    if (hasPageTitle || hasAttributes || hasActions) {
      didElementLoad('objectInfo');
    }
  }, []);

  if (!hasPageTitle && !hasAttributes && !hasActions) {
    return null;
  }

  const pageNameTag = breadcrumbs.length > 0 && pageTitleTag && (
    <LaunchFlag
      flag={{
        type: formatFlagType(pageTitleTag.type),
        label: pageTitleTag.label,
        description: pageTitleTag.description ?? pageTitleTag.label,
      }}
    />
  );

  return (
    <ThemeProvider theme={popoverThemeOverride}>
      {(hasPageTitle || hasAttributes || hasActions) && (
        <Container direction="column" rowGap="small">
          {hasPageTitle && (
            <PageTitleWrap
              hasActions={hasActions}
              hasAttributes={hasAttributes}
              isMobile={isMobileDevice}
              hasPageTitleIcon={!!pageTitleIcon}
            >
              <>
                <Title
                  objectType={objectType}
                  objectName={objectName}
                  pageName={pageName}
                  isMobile={isMobileDevice}
                  pageNameTag={pageNameTag}
                  pageTitleIconProps={pageTitleIcon}
                />
              </>
            </PageTitleWrap>
          )}
          {(hasAttributes || hasActions) && (
            <ActionsAndAttributesWrapper
              id="objectInfo"
              direction={isMobile ? 'column' : 'row'}
              justifyContent={
                !hasAttributes && hasActions ? 'flex-end' : 'space-between'
              }
              wrap="wrap"
              alignItems="center"
              rowGap="base"
            >
              {hasAttributes && (
                <Attributes
                  attributes={attributes}
                  errorMessage={attributeErrorMessage}
                />
              )}
              {hasActions && (
                <Actions actions={actions} errorMessage={actionErrorMessage} />
              )}
            </ActionsAndAttributesWrapper>
          )}
        </Container>
      )}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: RootState) => {
  const objectInfo = selectObjectInfo(state);
  return {
    attributes: objectInfo?.attributes || [],
    objectType: objectInfo?.objectType,
    objectName: objectInfo?.objectName,
    attributeErrorMessage: objectInfo?.attributeErrorMessage,
    actionErrorMessage: objectInfo?.actionErrorMessage,
    isMobileDevice: selectIsMobileWeb(state),
    pageTitleTag: selectRenderTagInPageName(state),
  };
};

export const ConnectedObjectInfo = connect(mapStateToProps)(ObjectInfo);
